/**
 * Lease markdown render pipeline (BR-17: single renderer).
 *
 * Lifted verbatim from Step8ReviewPreview so the lease wizard and the amendment flow render
 * through the same convertAnchorsToHtml → marked → DOMPurify pipeline.
 */

import { marked } from 'marked';
import DOMPurify from 'dompurify';
// Anchor token conversion — shared util from packages/lease-library/src/anchor-utils.ts.
// Imported here so the web preview renders anchor tokens identically to the PDF pipeline.
import { convertAnchorsToHtml } from '@resida/lease-library/anchor-utils';
import type { LeaseSection } from '../../pages/lease-wizard/steps/section-utils';

export interface TocEntry {
  id: string;
  title: string;
  level: 1 | 2 | 3;
}

export interface HeadingCounterState {
  section: number;
  subsection: number;
  subsubsection: number;
}

/**
 * Render Markdown to sanitized HTML with anchor conversion and TOC extraction.
 *
 * Pipeline:
 *   1. convertAnchorsToHtml: [[TYPE:role]] → .signature-anchor divs
 *   2. marked: Markdown → HTML, with custom heading renderer for IDs + TOC
 *   3. DOMPurify: sanitize (required by eslint-plugin-no-unsanitized)
 *
 * @param md - Markdown string to render
 * @param initialCounters - optional initial heading counter state for sequential section rendering
 * @returns { html, toc, finalCounters } — finalCounters allows chaining across sections
 */
export function renderMarkdownWithTocAndAnchors(
  md: string,
  initialCounters?: HeadingCounterState,
): { html: string; toc: TocEntry[]; finalCounters: HeadingCounterState } {
  const toc: TocEntry[] = [];
  let sectionCounter = initialCounters?.section ?? 0;
  let subsectionCounter = initialCounters?.subsection ?? 0;
  let subsubsectionCounter = initialCounters?.subsubsection ?? 0;

  const renderer = new marked.Renderer();

  renderer.heading = function (text: string, depth: number) {
    const slug = text
      .toLowerCase()
      .replace(/<[^>]*>/g, '')
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();

    if (depth === 1) {
      sectionCounter++;
      subsectionCounter = 0;
      subsubsectionCounter = 0;
    } else if (depth === 2) {
      subsectionCounter++;
      subsubsectionCounter = 0;
    } else if (depth === 3) {
      subsubsectionCounter++;
    }

    const id = `heading-${slug}-${sectionCounter}-${subsectionCounter}-${subsubsectionCounter}`;
    const cleanText = text.replace(/<[^>]*>/g, '');

    if (depth <= 3) {
      toc.push({ id, title: cleanText, level: depth as 1 | 2 | 3 });
    }

    return `<h${depth} id="${id}">${text}</h${depth}>`;
  };

  // Step 1: convert anchor tokens to HTML placeholders BEFORE marked
  // (anchor tokens are not Markdown and must not be processed as such)
  const withAnchors = convertAnchorsToHtml(md);

  // Step 2: Markdown → HTML
  const rawHtml = marked(withAnchors, { gfm: true, breaks: false, renderer }) as string;

  // Step 3: Sanitize — required by eslint-plugin-no-unsanitized
  // ADD_ATTR: 'id' for heading IDs, 'data-token' for anchor data attributes
  const html = DOMPurify.sanitize(rawHtml, {
    ADD_ATTR: ['id', 'data-token'],
    ADD_TAGS: ['span', 'div'],
  });

  return {
    html,
    toc,
    finalCounters: { section: sectionCounter, subsection: subsectionCounter, subsubsection: subsubsectionCounter },
  };
}

/**
 * Render all sections sequentially, threading heading counter state between them.
 * This ensures heading IDs in the per-section DOM match those from a full-document render.
 */
export function renderSectionsSequentially(
  sections: LeaseSection[],
): Array<{ html: string; sectionIndex: number; beforeCounters: HeadingCounterState }> {
  let counters: HeadingCounterState = { section: 0, subsection: 0, subsubsection: 0 };
  return sections.map((section, i) => {
    // Counter state ENTERING this section — applied as an inline `counter-reset` on the
    // section wrapper so CSS heading numbering continues across per-section wrappers.
    // (Each section renders in its own <div>; without this, the subsection counter — reset
    // at the h1 element level — is scoped to one wrapper and every `##` restarts at .1.)
    const beforeCounters = counters;
    const { html, finalCounters } = renderMarkdownWithTocAndAnchors(section.content, beforeCounters);
    counters = finalCounters;
    return { html, sectionIndex: i, beforeCounters };
  });
}
